import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { buildSupabaseHeaders, supabase, isMockMode } from '@/lib/supabase';
import { getValidAccessToken } from '@/lib/auth/session';
import { DBUser, ProviderInfo } from '@/types';

interface AuthContextType {
    user: DBUser | null;
    loading: boolean;
    isMockMode: boolean;
    signOut: () => Promise<void>;
    signInMock: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Mock user for demo mode
const MOCK_USER: DBUser = {
    id: 'mock-user-001',
    provider: 'github',
    provider_id: '123456',
};

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<DBUser | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isMockMode) {
            const hasSession = localStorage.getItem('mock_session');
            if (hasSession === 'true') {
                setUser(MOCK_USER);
            }
            setLoading(false);
            return;
        }

        if (!supabase) {
            setLoading(false);
            return;
        }

        // Get initial session
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session) {
                handleSession(session);
            }
            setLoading(false);
        }).catch((err) => {
            console.error('Failed to get session:', err);
            setLoading(false);
        });

        // Listen for auth changes — Supabase handles token refresh and expiry internally
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            if (session) {
                handleSession(session);
            } else {
                setUser(null);
            }
        });

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    function signInMock() {
        localStorage.setItem('mock_session', 'true');
        setUser(MOCK_USER);
    }

    async function signOut() {
        if (isMockMode) {
            localStorage.removeItem('mock_session');
            setUser(null);
            return;
        }
        await supabase?.auth.signOut();
        localStorage.removeItem('login_timestamp');
        setUser(null);
    }

    async function handleSession(session: any) {
        try {
            const userMeta = session.user.user_metadata;
            const provider = session.user.app_metadata.provider;
            const provider_id = userMeta.sub || session.user.id;

            setUser({
                id: session.user.id,
                provider,
                provider_id,
            });

            const accessToken = await getValidAccessToken();
            if (!accessToken) {
                await supabase?.auth.signOut();
                setUser(null);
                return;
            }

            if (supabase) {
                const { error: bootstrapError } = await supabase.functions.invoke('bootstrap-user', {
                    body: {},
                    headers: buildSupabaseHeaders(accessToken),
                });

                if (bootstrapError) {
                    console.warn('bootstrap-user failed during session sync:', bootstrapError);
                }
            }

            // In a real app, you'd call your edge function for daily login here
        } catch (err) {
            console.error('Auth session error:', err);
        }
    }

    return (
        <AuthContext.Provider value={{ user, loading, isMockMode, signOut, signInMock }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
